/**
 * Utility functions for consistent brand name and module formatting
 * 
 * These functions implement the brand style guidelines for displaying
 * product names and modules throughout the site.
 */

import React from 'react';

// Terms that should be in bold and uppercase
const boldUppercaseTerms = [
  'OPUS',
  'INFORTE',
  'ODOO',
  'ECOSOFT',
  'OPUS ERP'
];

// Module names in bold uppercase format
const boldUppercaseModules = [
  'OPUS M1 PRESUPUESTO PROGRAMABLE',
  'OPUS M2 PLANEACIÓN Y CONTROL INTEGRAL',
  'OPUS M3 ADMINISTRACIÓN DE COMPRAS',
  'OPUS M4 DEPENDENCIAS',
  'OPUS M4.1 EVALUADOR DE PROPUESTAS',
  'OPUS M4.2 BASES DE CONCURSO',
  'OPUS M5 CUANTIFICACIÓN BIM'
];

// Module names in bold format (mixed case)
const boldModules = [
  'OPUS Módulo 1 PRESUPUESTO PROGRAMABLE',
  'OPUS Módulo 2 PLANEACIÓN Y CONTROL INTEGRAL',
  'OPUS Módulo 3 ADMINISTRACIÓN DE COMPRAS',
  'OPUS Módulo 4 DEPENDENCIAS',
  'OPUS Módulo 5 CUANTIFICACIÓN BIM'
];

/**
 * Applies brand styling to text containing product names and modules
 * 
 * @param {string} text - The text to process
 * @return {JSX.Element[]} - An array of text and styled spans
 */
export const formatBrandText = (text) => {
  if (!text) return text;
  
  // Create a copy of the text to work with
  let workingText = text;
  let result = [];
  let lastIndex = 0;
  
  // Process all terms that need special formatting
  const allTerms = [
    ...boldUppercaseTerms.map(term => ({ term, style: 'bold-uppercase' })),
    ...boldUppercaseModules.map(term => ({ term, style: 'bold-uppercase' })),
    ...boldModules.map(term => ({ term, style: 'bold' }))
  ];
  
  // Sort terms by length (longest first) to avoid partial matches
  allTerms.sort((a, b) => b.term.length - a.term.length);
  
  // Find all occurrences of each term
  const matches = [];
  
  allTerms.forEach(({ term, style }) => {
    // Case-insensitive search for the term
    const regex = new RegExp(term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    let match;
    
    while ((match = regex.exec(workingText)) !== null) {
      matches.push({
        term: match[0],
        style,
        index: match.index,
        endIndex: match.index + match[0].length
      });
    }
  });
  
  // Sort matches by their position in the text
  matches.sort((a, b) => a.index - b.index);
  
  // Filter out overlapping matches
  const filteredMatches = [];
  let lastEnd = 0;
  
  matches.forEach(match => {
    if (match.index >= lastEnd) {
      filteredMatches.push(match);
      lastEnd = match.endIndex;
    }
  });
  
  // Build result with styled spans
  filteredMatches.forEach((match, i) => {
    // Add text before this match
    if (match.index > lastIndex) {
      result.push(workingText.substring(lastIndex, match.index));
    }
    
    // Add the styled match
    if (match.style === 'bold-uppercase') {
      result.push(
        <strong key={`term-${i}`} className="uppercase">
          {match.term.toUpperCase()}
        </strong>
      );
    } else if (match.style === 'bold') {
      result.push(
        <strong key={`term-${i}`}>
          {match.term}
        </strong>
      );
    }
    
    lastIndex = match.endIndex;
  });
  
  // Add any remaining text
  if (lastIndex < workingText.length) {
    result.push(workingText.substring(lastIndex));
  }
  
  return result;
};

/**
 * React component that wraps text with brand styling
 * 
 * @param {object} props - Component props
 * @param {string} props.children - The text to style
 * @param {string} props.className - Additional CSS classes
 * @return {JSX.Element} - The styled text
 */
export const BrandText = ({ children, className = '', ...props }) => {
  if (typeof children !== 'string') {
    return <span className={className} {...props}>{children}</span>;
  }
  
  return (
    <span className={className} {...props}>
      {formatBrandText(children)}
    </span>
  );
};

export default BrandText; 